import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuotesStore } from '../quotesStore';
import type {
  QuoteCreateRequest,
  InsurancePlan,
  HandbookPlan,
  LengthOfLeaveWeeks,
  RaterFile,
  PolicyState,
  PowerPointTemplate,
  PEOGroup,
  CensusIgnoredReasons,
} from '../types';
import { quotesService } from '../quotesService';
import {
  QUOTE_TYPE_OPTIONS,
  DISTRIBUTION_CHANNEL_OPTIONS,
  AGGREGATE_BENEFIT_DEFAULT,
  QUOTE_STATUS_OPTIONS,
  POLICY_STATE_OPTIONS,
  BENEFITS_OPTIONS,
  LENGTH_OF_LEAVE_WEEKS_OPTIONS,
  LENGTH_OF_LEAVE_WEEKS_SEC_OPTIONS,
  LENGTH_OF_SERVICE_OPTIONS,
  CONCURRENCY_OPTIONS,
  YES_NO_OPTIONS,
  INCLUDE_LMS_OPTIONS,
  PLAN_TYPE_OPTIONS,
  AGGREGATE_BENEFIT_OPTIONS,
  LENGTH_OF_LEAVE_WEEKS_SELECT_OPTIONS,
} from '../constants';
import { Input } from '../../../components/forms/Input';
import { Select } from '../../../components/forms/Select';
import { DatePicker } from '../../../components/forms/DatePicker/DatePicker';
import { Tooltip } from '../../../components/Tooltip';
import { cn } from '../../../utils/cn';
import { parseMoneyToDecimalString } from '../utils/money';
import { FileUpload } from './FileUpload';

const EMPTY_CENSUS_IGNORED_REASONS: CensusIgnoredReasons = {
  state_not_allowed: 0,
  annual_salary_empty: 0,
  annual_salary_lt_15000: 0,
  age_out_of_range: 0,
  birth_date_empty: 0,
  employed_date_empty: 0,
  under_18_at_effective_date: 0,
};

const CENSUS_IGNORED_REASON_LABELS: Record<keyof CensusIgnoredReasons, string> = {
  state_not_allowed: 'State is not one of the 50 states or Puerto Rico',
  annual_salary_empty: 'Annual salary is empty',
  annual_salary_lt_15000: 'Annual salary is less than 15000',
  age_out_of_range: 'Age is outside supported range',
  birth_date_empty: 'Birth date is empty',
  employed_date_empty: 'Employed date is empty',
  under_18_at_effective_date: 'Under 18 as of the policy effective date',
};

// BENEFITS_OPTIONS used to offer '66.7%'; it now offers the more precise
// '66.67%'. Quotes saved under the old label (as a benefit or as
// pay_for_std_plan) are remapped on load so the edit screen still shows
// them as selected against the current options.
const LEGACY_BENEFIT_VALUE_MAP: Record<string, string> = {
  '66.7%': '66.67%',
  '66.7': '66.67',
};

const normalizeInsurancePlan = (plan: InsurancePlan): InsurancePlan => ({
  ...plan,
  benefits: plan.benefits?.map((benefit) => LEGACY_BENEFIT_VALUE_MAP[benefit] || benefit) ?? plan.benefits,
  pay_for_std_plan: plan.pay_for_std_plan
    ? LEGACY_BENEFIT_VALUE_MAP[plan.pay_for_std_plan] || plan.pay_for_std_plan
    : plan.pay_for_std_plan,
});

export const QuoteFormComponent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;
  const prefillData = location.state?.prefill;
  const isClone = !!prefillData;

  const {
    currentQuote,
    fetchQuoteDetail,
    createQuote: createQuoteAction,
    updateQuote: updateQuoteAction,
  } = useQuotesStore();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [raterFiles, setRaterFiles] = useState<RaterFile[]>([]);
  const [powerPointTemplates, setPowerPointTemplates] = useState<PowerPointTemplate[]>([]);
  const [peoGroups, setPeoGroups] = useState<PEOGroup[]>([]);
  const [employeesIgnoredCount, setEmployeesIgnoredCount] = useState(0);
  const [censusIgnoredReasons, setCensusIgnoredReasons] = useState<CensusIgnoredReasons>(
    EMPTY_CENSUS_IGNORED_REASONS
  );

  const [formData, setFormData] = useState<Partial<QuoteCreateRequest> & { rater_file?: number }>({
    prospect_name: '',
    hubspot_company_id: null,
    quote_name: '',
    quote_type: 'new',
    distribution_channel: 'direct',
    peo_group: null,
    policy_state: undefined,
    policy_effective_date: '',
    status: 'drafted',
    employee_count: 0,
    version: 1,
    sic_code: '',
    for_profit_business: 'yes',
    power_point_template: null,
  });

  const [insurancePlan, setInsurancePlan] = useState<Partial<InsurancePlan>>({
    benefits: [],
    weekly_cap_1: '',
    weekly_cap_2: null,
    weekly_cap_3: null,
    length_of_leave_weeks: [],
    length_of_leave_weeks_sec: null,
    length_of_service: [],
    concurrency_with_benefits: 'concurrently',
    private_std_plan: 'no',
    pay_for_std_plan: null,
    maximum_weekly_payment: null,
    waiting_period_days: null,
    // New quotes default to Yes; clone/edit overwrite the whole insurance plan
    // from the source quote below, so previous settings are still honored.
    sdi_offset: 'yes',
    pfl_offset: 'yes',
    include_lms: 'yes',
    aggregate_benefit: AGGREGATE_BENEFIT_DEFAULT,
  });

  const [handbookPlan, setHandbookPlan] = useState<Partial<HandbookPlan>>({
    plan_type: 'standard',
    length_of_leave_weeks: '16',
    length_of_leave_weeks_sec: null,
    benefit: '100.00',
    weekly_cap: '3000',
  });

  // PRT0-4(c): on the Create page these Handbook fields auto-default from the
  // first-selected Insurance value, until the user overrides them by hand. Once
  // a field is manually edited we stop mirroring it.
  const handbookManuallyEditedRef = useRef<{
    length_of_leave_weeks: boolean;
    benefit: boolean;
    weekly_cap: boolean;
  }>({ length_of_leave_weeks: false, benefit: false, weekly_cap: false });

  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    // Convert YYYY-MM-DD to mm/dd/yyyy
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${month}/${day}/${year}`;
    }
    return dateString;
  };

  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    // Handle mm/dd/yyyy format
    if (dateString.includes('/')) {
      const [month, day, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // Handle dd.mm.yyyy format (legacy)
    if (dateString.includes('.')) {
      const [day, month, year] = dateString.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // Already in YYYY-MM-DD format
    return dateString;
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [files, templates, groups] = await Promise.all([
          quotesService.getRaterFiles({ is_active: true }),
          quotesService.getPowerPointTemplates(),
          quotesService.getPeoGroups(),
        ]);
        const validFiles = Array.isArray(files) ? files : [];
        setRaterFiles(validFiles);
        const validTemplates = Array.isArray(templates) ? templates : [];
        setPowerPointTemplates(validTemplates);
        setPeoGroups(Array.isArray(groups) ? groups.filter((g) => g.is_active) : []);

        if (!isEdit && !isClone && validFiles.length > 0) {
          const mostRecent = validFiles.sort((a, b) => 
            new Date(b.created).getTime() - new Date(a.created).getTime()
          )[0];
          
          if (mostRecent) {
            setFormData((prev) => ({
              ...prev,
              rater_file: prev.rater_file ?? mostRecent.id,
            }));
          }
        }

        if (!isEdit && !formData.power_point_template && validTemplates.length > 0) {
          setFormData((prev) => ({
            ...prev,
            power_point_template: validTemplates[0].id,
          }));
        }
      } catch {
        setError('Failed to load form options. Please refresh the page.');
        setRaterFiles([]);
        setPowerPointTemplates([]);
      }
    };

    fetchDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      fetchQuoteDetail(Number(id));
    } else if (prefillData) {
      const quote = prefillData;
      setFormData({
        prospect_name: quote.prospect_name || '',
        hubspot_company_id: quote.hubspot_company_id ?? null,
        quote_name: quote.quote_name || '',
        quote_type: quote.quote_type || 'new',
        distribution_channel: quote.distribution_channel || 'direct',
        peo_group: quote.peo_group?.id ?? null,
        policy_state: quote.policy_state || undefined,
        policy_effective_date: quote.policy_effective_date ? formatDateForDisplay(quote.policy_effective_date) : '',
        status: quote.status || 'drafted',
        employee_count: quote.employee_count || 0,
        version: quote.next_chain_version || (quote.version || 0) + 1,
        rater_file: quote.rater_file?.id,
        power_point_template: quote.power_point_template?.id ?? null,
        sic_code: quote.sic_code || '',
        for_profit_business: quote.for_profit_business || 'yes',
        source_quote_id: quote.id,
      });
      setEmployeesIgnoredCount(quote.employees_ignored_count || 0);
      setCensusIgnoredReasons(quote.census_ignored_reasons || EMPTY_CENSUS_IGNORED_REASONS);
      
      if (quote.insurance_plan) {
        setInsurancePlan(normalizeInsurancePlan(quote.insurance_plan));
      }
      
      if (quote.handbook_plan) {
        setHandbookPlan(quote.handbook_plan);
      }

      // PRT0-2(a): the census file is cloned server-side on create (see
      // QuoteWriteSerializer.create). The old browser re-fetch of the census
      // relied on the presigned media URL and regressed, so it's gone; the
      // form leaves the census upload empty and the backend copies it from the
      // source quote. The user can still upload a different census to override.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, prefillData]);

  useEffect(() => {
    if (currentQuote && isEdit) {
      setFormData({
        prospect_name: currentQuote.prospect_name,
        hubspot_company_id: currentQuote.hubspot_company_id ?? null,
        quote_name: currentQuote.quote_name,
        quote_type: currentQuote.quote_type,
        distribution_channel: currentQuote.distribution_channel,
        peo_group: currentQuote.peo_group?.id ?? null,
        policy_state: currentQuote.policy_state,
        policy_effective_date: formatDateForDisplay(currentQuote.policy_effective_date),
        status: currentQuote.status,
        employee_count: currentQuote.employee_count,
        version: currentQuote.version,
        rater_file: currentQuote.rater_file?.id,
        power_point_template: currentQuote.power_point_template?.id ?? null,
        sic_code: currentQuote.sic_code || '',
        for_profit_business: currentQuote.for_profit_business || 'yes',
      });
      setEmployeesIgnoredCount(currentQuote.employees_ignored_count || 0);
      setCensusIgnoredReasons(currentQuote.census_ignored_reasons || EMPTY_CENSUS_IGNORED_REASONS);

      if (currentQuote.insurance_plan) {
        setInsurancePlan(normalizeInsurancePlan(currentQuote.insurance_plan));
      }

      if (currentQuote.handbook_plan) {
        setHandbookPlan(currentQuote.handbook_plan);
      }
    }
  }, [currentQuote, isEdit]);

  const handleInputChange = (field: keyof QuoteCreateRequest, value: string | number | File | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleInsurancePlanChange = (field: keyof InsurancePlan, value: string | string[] | number | null) => {
    setInsurancePlan((prev) => ({ ...prev, [field]: value }));
  };

  const handleHandbookPlanChange = (field: keyof HandbookPlan, value: string | null) => {
    // PRT0-4(c): a manual edit to an auto-defaulted field stops future mirroring.
    if (
      field === 'length_of_leave_weeks' ||
      field === 'benefit' ||
      field === 'weekly_cap'
    ) {
      handbookManuallyEditedRef.current[field] = true;
    }
    setHandbookPlan((prev) => ({ ...prev, [field]: value }));
  };

  // PRT0-4(c): auto-default the Handbook Length of Leave Weeks, Benefit and
  // Weekly Cap from the first-selected Insurance values on the Create page.
  // Skipped for edit/clone (those load their own saved Handbook plan) and for
  // any field the user has manually overridden. When Insurance Weekly Cap is
  // left blank the Handbook cap is filled server-side after the rater computes
  // the recommended cap (see QuoteOutputGenerator._maybe_fill_handbook_weekly_cap).
  useEffect(() => {
    if (isEdit || isClone) return;
    const edited = handbookManuallyEditedRef.current;
    setHandbookPlan((prev) => {
      const next = { ...prev };
      const firstLol = insurancePlan.length_of_leave_weeks?.[0];
      if (!edited.length_of_leave_weeks && firstLol) {
        next.length_of_leave_weeks = firstLol as LengthOfLeaveWeeks;
      }
      const firstBenefit = insurancePlan.benefits?.[0];
      if (!edited.benefit && firstBenefit) {
        next.benefit = firstBenefit.replace('%', '').trim();
      }
      if (!edited.weekly_cap && insurancePlan.weekly_cap_1) {
        next.weekly_cap = insurancePlan.weekly_cap_1;
      }
      return next;
    });
  }, [
    insurancePlan.length_of_leave_weeks,
    insurancePlan.benefits,
    insurancePlan.weekly_cap_1,
    isEdit,
    isClone,
  ]);

  const handleBenefitToggle = (benefit: string) => {
    setInsurancePlan((prev) => {
      const current = prev.benefits || [];
      if (current.includes(benefit)) {
        return { ...prev, benefits: current.filter((b) => b !== benefit) };
      }
      if (current.length >= 3) {
        return prev;
      }
      return { ...prev, benefits: [...current, benefit] };
    });
  };

  const handleLengthOfLeaveToggle = (weeks: string) => {
    setInsurancePlan((prev) => {
      const current = prev.length_of_leave_weeks || [];
      if (current.includes(weeks)) {
        return { ...prev, length_of_leave_weeks: current.filter((w) => w !== weeks) };
      }
      if (current.length >= 3) {
        return prev;
      }
      return { ...prev, length_of_leave_weeks: [...current, weeks] };
    });
  };

  const handleLengthOfLeaveSecToggle = (weeks: string) => {
    setInsurancePlan((prev) => {
      const current = prev.length_of_leave_weeks_sec || [];
      if (current.includes(weeks)) {
        return { ...prev, length_of_leave_weeks_sec: current.filter((w) => w !== weeks) };
      }
      if (current.length >= 2) {
        return prev;
      }
      return { ...prev, length_of_leave_weeks_sec: [...current, weeks] };
    });
  };

  const handleLengthOfServiceToggle = (service: string) => {
    setInsurancePlan((prev) => {
      const current = prev.length_of_service || [];
      if (current.includes(service)) {
        return { ...prev, length_of_service: current.filter((s) => s !== service) };
      }
      if (current.length >= 2) {
        return prev;
      }
      return { ...prev, length_of_service: [...current, service] };
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.prospect_name?.trim()) {
      errors.prospect_name = 'Prospect name is required';
    }
    if (formData.hubspot_company_id === null || formData.hubspot_company_id === undefined || String(formData.hubspot_company_id).trim() === '') {
      errors.hubspot_company_id = 'Hubspot Company ID is required';
    } else if (!/^\d+$/.test(String(formData.hubspot_company_id).trim())) {
      errors.hubspot_company_id = 'Hubspot Company ID must be a numeric value';
    }
    if (!formData.quote_name?.trim()) {
      errors.quote_name = 'Quote name is required';
    }
    if (!formData.policy_effective_date) {
      errors.policy_effective_date = 'Policy effective date is required';
    }
    if (!formData.policy_state) {
      errors.policy_state = 'Policy state is required';
    }
    if (!formData.status) {
      errors.status = 'Status is required';
    }
    if (!isEdit && !formData.rater_file) {
      errors.rater_file = 'Rater file is required';
    }
    if (!isEdit && !isClone && !formData.census_file) {
      errors.census_file = 'Census file is required';
    }

    const hasInsurancePlanData = (insurancePlan.benefits?.length ?? 0) > 0 || 
      insurancePlan.weekly_cap_1 || 
      (insurancePlan.length_of_leave_weeks?.length ?? 0) > 0 ||
      (insurancePlan.length_of_service?.length ?? 0) > 0;
    
    if (hasInsurancePlanData) {
      if (!insurancePlan.benefits || insurancePlan.benefits.length === 0) {
        errors.insurance_benefits = 'At least one benefit is required';
      } else if (insurancePlan.benefits.length > 3) {
        errors.insurance_benefits = 'Maximum 3 benefits allowed';
      }
      
      // Item 40: Weekly Cap is optional. When blank, the rater computes a
      // recommended cap. Validate the range only when a value is provided.
      if (insurancePlan.weekly_cap_1) {
        const cap1 = parseFloat(insurancePlan.weekly_cap_1);
        if (isNaN(cap1) || cap1 < 0 || cap1 > 4000) {
          errors.weekly_cap_1 = 'Weekly cap 1 must be between $0 and $4,000';
        }
      }

      if (insurancePlan.weekly_cap_2) {
        const cap2 = parseFloat(insurancePlan.weekly_cap_2);
        if (isNaN(cap2) || cap2 < 0 || cap2 > 4000) {
          errors.weekly_cap_2 = 'Weekly cap 2 must be between $0 and $4,000';
        }
      }

      if (insurancePlan.weekly_cap_3) {
        const cap3 = parseFloat(insurancePlan.weekly_cap_3);
        if (isNaN(cap3) || cap3 < 0 || cap3 > 4000) {
          errors.weekly_cap_3 = 'Weekly cap 3 must be between $0 and $4,000';
        }
      }
      
      if (!insurancePlan.length_of_leave_weeks || insurancePlan.length_of_leave_weeks.length === 0) {
        errors.length_of_leave_weeks = 'At least one length of leave weeks is required';
      } else if (insurancePlan.length_of_leave_weeks.length > 3) {
        errors.length_of_leave_weeks = 'Maximum 3 selections allowed';
      }
      
      if (insurancePlan.length_of_leave_weeks_sec && insurancePlan.length_of_leave_weeks_sec.length > 2) {
        errors.length_of_leave_weeks_sec = 'Maximum 2 selections allowed';
      }
      
      if (!insurancePlan.length_of_service || insurancePlan.length_of_service.length === 0) {
        errors.length_of_service = 'At least one length of service is required';
      } else if (insurancePlan.length_of_service.length > 2) {
        errors.length_of_service = 'Maximum 2 selections allowed';
      }
      
      if (insurancePlan.private_std_plan === 'yes') {
        if (!insurancePlan.pay_for_std_plan) {
          errors.pay_for_std_plan = 'STD Benefit Percentage is required when Private STD Plan is Yes';
        } else {
          const percentage = parseFloat(insurancePlan.pay_for_std_plan);
          if (isNaN(percentage) || percentage < 0 || percentage > 100) {
            errors.pay_for_std_plan = 'STD Benefit Percentage must be between 0 and 100';
          }
        }
        if (!insurancePlan.maximum_weekly_payment) {
          errors.maximum_weekly_payment = 'Weekly max is required';
        } else {
          const amount = parseFloat(insurancePlan.maximum_weekly_payment);
          if (isNaN(amount) || amount < 100 || amount > 4000) {
            errors.maximum_weekly_payment = 'Weekly max must be between $100 and $4,000';
          }
        }
        if (!insurancePlan.waiting_period_days) {
          errors.waiting_period_days = 'Waiting period days is required when Private STD Plan is Yes';
        } else {
          const days = insurancePlan.waiting_period_days;
          if (typeof days !== 'number' || isNaN(days) || days < 4 || days > 30) {
            errors.waiting_period_days = 'Waiting period must be between 4 and 30 days';
          }
        }
      }
    }

    const hasHandbookPlanData = handbookPlan.plan_type || 
      handbookPlan.length_of_leave_weeks || 
      handbookPlan.weekly_cap;
    
    if (hasHandbookPlanData) {
      if (!handbookPlan.length_of_leave_weeks) {
        errors.handbook_length_of_leave_weeks = 'Length of leave weeks is required';
      }
      
      if (!handbookPlan.benefit) {
        errors.handbook_benefit = 'Benefit is required';
      } else {
        const benefit = parseFloat(handbookPlan.benefit);
        if (isNaN(benefit) || benefit <= 0) {
          errors.handbook_benefit = 'Benefit must be a positive number';
        }
      }
      
      if (!handbookPlan.weekly_cap) {
        errors.handbook_weekly_cap = 'Weekly cap is required';
      } else {
        const cap = parseFloat(handbookPlan.weekly_cap);
        if (isNaN(cap) || cap <= 0) {
          errors.handbook_weekly_cap = 'Weekly cap must be a positive number';
        }
      }
      
      if (handbookPlan.plan_type === 'primary_and_secondary' && !handbookPlan.length_of_leave_weeks_sec) {
        errors.handbook_length_of_leave_weeks_sec = 'Length of leave weeks secondary is required for Primary and Secondary plan';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const preparedInsurancePlan: InsurancePlan | undefined = insurancePlan.benefits && insurancePlan.benefits.length > 0 ? {
        benefits: insurancePlan.benefits,
        weekly_cap_1: parseMoneyToDecimalString(insurancePlan.weekly_cap_1) || '',
        weekly_cap_2: parseMoneyToDecimalString(insurancePlan.weekly_cap_2),
        weekly_cap_3: parseMoneyToDecimalString(insurancePlan.weekly_cap_3),
        length_of_leave_weeks: insurancePlan.length_of_leave_weeks || [],
        length_of_leave_weeks_sec: insurancePlan.length_of_leave_weeks_sec || null,
        length_of_service: insurancePlan.length_of_service || [],
        concurrency_with_benefits: insurancePlan.concurrency_with_benefits || 'concurrently',
        private_std_plan: insurancePlan.private_std_plan || 'no',
        pay_for_std_plan: parseMoneyToDecimalString(insurancePlan.pay_for_std_plan),
        maximum_weekly_payment: parseMoneyToDecimalString(insurancePlan.maximum_weekly_payment),
        waiting_period_days: insurancePlan.waiting_period_days || null,
        sdi_offset: insurancePlan.sdi_offset || 'no',
        pfl_offset: insurancePlan.pfl_offset || 'no',
        include_lms: insurancePlan.include_lms || 'yes',
        aggregate_benefit: insurancePlan.aggregate_benefit || '1000000',
      } : undefined;

      const preparedHandbookPlan: HandbookPlan | undefined = handbookPlan.plan_type && handbookPlan.length_of_leave_weeks && handbookPlan.weekly_cap ? {
        plan_type: handbookPlan.plan_type,
        length_of_leave_weeks: handbookPlan.length_of_leave_weeks as LengthOfLeaveWeeks,
        length_of_leave_weeks_sec: handbookPlan.length_of_leave_weeks_sec as LengthOfLeaveWeeks | null,
        benefit: parseMoneyToDecimalString(handbookPlan.benefit) || '100.00',
        weekly_cap: parseMoneyToDecimalString(handbookPlan.weekly_cap) || '',
      } : undefined;

      if (!formData.prospect_name || !formData.quote_name || !formData.policy_effective_date || !formData.rater_file) {
        setError('Please fill in all required fields');
        return;
      }

      const submitData: QuoteCreateRequest = {
        prospect_name: formData.prospect_name,
        hubspot_company_id: formData.hubspot_company_id ?? null,
        quote_name: formData.quote_name,
        quote_type: formData.quote_type || 'new',
        distribution_channel: formData.distribution_channel || 'direct',
        peo_group: formData.peo_group ?? null,
        policy_state: (formData.policy_state || undefined) as PolicyState,
        policy_effective_date: formatDateForAPI(formData.policy_effective_date),
        rater_file: formData.rater_file,
        insurance_plan: preparedInsurancePlan,
        handbook_plan: preparedHandbookPlan,
        census_file: formData.census_file,
        employee_count: formData.employee_count,
        version: formData.version,
        sic_code: formData.sic_code || '',
        for_profit_business: formData.for_profit_business || 'yes',
        power_point_template: formData.power_point_template ?? null,
        source_quote_id: formData.source_quote_id,
      };

      let result;
      if (isEdit && id) {
        result = await updateQuoteAction(Number(id), submitData);
      } else {
        result = await createQuoteAction(submitData);
      }

      navigate(`/quotes/${result.id}`);
    } catch (err) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { field_errors?: Record<string, string | string[]> } } };
        if (axiosError.response?.data?.field_errors) {
          const fieldErrors: Record<string, string> = {};
          Object.entries(axiosError.response.data.field_errors).forEach(([key, value]) => {
            fieldErrors[key] = Array.isArray(value) ? value[0] : String(value);
          });
          setFormErrors(fieldErrors);
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Failed to save quote');
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="max-w-7xl">
      <div className="mb-6">
        <button
          onClick={() => navigate('/quotes')}
          className="mb-2 text-sm font-medium"
          style={{ color: 'var(--color-primary-500)' }}
        >
          ← Back to Quotes
        </button>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
          {isEdit ? 'Edit Quote' : 'Create Quote'}
        </h1>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border p-4" style={{ backgroundColor: 'var(--color-error-50)', borderColor: 'var(--color-error-500)' }}>
          <p style={{ color: 'var(--color-error-700)' }}>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div
          className="rounded-lg border p-5"
          style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Prospect Name"
              value={formData.prospect_name}
              onChange={(e) => handleInputChange('prospect_name', e.target.value)}
              errorText={formErrors.prospect_name}
              required
              fullWidth
            />
            <Input
              label="Hubspot Company ID"
              type="number"
              value={formData.hubspot_company_id != null ? String(formData.hubspot_company_id) : ''}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange('hubspot_company_id', value === '' ? null : Number(value));
                if (formErrors.hubspot_company_id) {
                  setFormErrors((prev) => ({ ...prev, hubspot_company_id: '' }));
                }
              }}
              errorText={formErrors.hubspot_company_id}
              required
              fullWidth
            />
            <Input
              label="Quote Name"
              value={formData.quote_name}
              onChange={(e) => handleInputChange('quote_name', e.target.value)}
              errorText={formErrors.quote_name}
              required
              fullWidth
            />
            <Select
              label="Quote Type"
              options={QUOTE_TYPE_OPTIONS}
              value={formData.quote_type}
              onChange={(e) => handleInputChange('quote_type', e.target.value)}
              required
              fullWidth
            />
            <Select
              label="Distribution Channel"
              options={DISTRIBUTION_CHANNEL_OPTIONS}
              value={formData.distribution_channel}
              onChange={(e) => handleInputChange('distribution_channel', e.target.value)}
              required
              fullWidth
            />
            <div>
              <label
                htmlFor="peo_group"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--color-text-primary)' }}
              >
                PEO Group
              </label>
              <Select
                id="peo_group"
                value={formData.peo_group ? String(formData.peo_group) : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    peo_group: val ? parseInt(val, 10) : null,
                  }));
                }}
                options={[
                  { value: '', label: '' },
                  ...peoGroups.map((g) => ({ value: String(g.id), label: g.name })),
                ]}
                fullWidth
              />
            </div>
            <div>
              <label
                htmlFor="power_point_template"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--color-text-primary)' }}
              >
                PowerPoint Template
              </label>
              <Select
                id="power_point_template"
                value={formData.power_point_template ? String(formData.power_point_template) : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    power_point_template: val ? parseInt(val, 10) : null,
                  }));
                }}
                options={[
                  { value: '', label: 'Select a PowerPoint template...' },
                  ...powerPointTemplates.map((template) => ({
                    value: template.id.toString(),
                    label: template.display_name,
                  })),
                ]}
                fullWidth
              />
              {powerPointTemplates.length === 0 && (
                <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  No active PowerPoint templates available
                </p>
              )}
            </div>
            <Input
              label="SIC Code"
              value={formData.sic_code || ''}
              onChange={(e) => handleInputChange('sic_code', e.target.value)}
              maxLength={50}
              fullWidth
            />
            <Select
              label="For-Profit Business"
              options={YES_NO_OPTIONS}
              value={formData.for_profit_business || 'yes'}
              onChange={(e) => handleInputChange('for_profit_business', e.target.value)}
              required
              fullWidth
            />
          </div>
        </div>

        <div
          className="rounded-lg border p-5"
          style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Policy Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Policy State"
              options={POLICY_STATE_OPTIONS}
              value={formData.policy_state || ''}
              onChange={(e) => handleInputChange('policy_state', e.target.value)}
              placeholder="Select a state"
              required
              fullWidth
              errorText={formErrors.policy_state}
            />
            <DatePicker
              label="Policy Effective Date"
              placeholder="mm/dd/yyyy"
              value={formData.policy_effective_date}
              onChange={(date) => handleInputChange('policy_effective_date', date)}
              errorText={formErrors.policy_effective_date}
              required
              fullWidth
            />
            <Select
              label="Status"
              options={QUOTE_STATUS_OPTIONS}
              value={formData.status || ''}
              onChange={(e) => handleInputChange('status', e.target.value)}
              placeholder="Select a status"
              required
              fullWidth
              errorText={formErrors.status}
            />
            <div>
              <label
                htmlFor="rater_file"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Rater File <span style={{ color: 'var(--color-error-500)' }}>*</span>
              </label>
              <Select
                id="rater_file"
                value={formData.rater_file && formData.rater_file > 0 ? formData.rater_file.toString() : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    rater_file: val ? parseInt(val) : undefined,
                  }));
                }}
                options={[
                  { value: '', label: 'Select a rater file...' },
                  ...(Array.isArray(raterFiles) ? raterFiles.map((file) => ({
                    value: file.id.toString(),
                    label: file.display_name || file.custom_file_name || file.file,
                  })) : []),
                ]}
                required={!isEdit}
                fullWidth
                disabled={isEdit}
              />
              {formErrors.rater_file && (
                <p className="mt-1 text-sm" style={{ color: 'var(--color-error-500)' }}>
                  {formErrors.rater_file}
                </p>
              )}
            </div>
          </div>
        </div>

        <div
          className="rounded-lg border p-5"
          style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Census & Employee Information
          </h2>
          <div className="space-y-4">
            <FileUpload
              label="Census File"
              value={formData.census_file || null}
              onChange={(file) => handleInputChange('census_file', file)}
              errorText={formErrors.census_file}
              required={!isEdit && !isClone}
            />
            {isClone && !formData.census_file && (
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                The census file will be copied from the source quote. Upload a file to use a different census.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Employees Accepted"
                type="number"
                value={formData.employee_count?.toString() || '0'}
                onChange={(e) => handleInputChange('employee_count', parseInt(e.target.value) || 0)}
                required
                fullWidth
                readOnly
              />
              <div>
                <label
                  htmlFor="employees-ignored"
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Employees Ignored
                  <Tooltip
                    content={
                      <div className="space-y-1">
                        {(Object.keys(CENSUS_IGNORED_REASON_LABELS) as Array<keyof CensusIgnoredReasons>).map(
                          (key) => (
                            <div key={key}>
                              {CENSUS_IGNORED_REASON_LABELS[key]}: {censusIgnoredReasons[key] ?? 0}
                            </div>
                          )
                        )}
                      </div>
                    }
                  >
                    <span
                      className="ml-2 inline-flex items-center justify-center cursor-help rounded-full text-[10px] font-bold leading-none w-4 h-4"
                      aria-label="Employees ignored reasons"
                      style={{
                        backgroundColor: 'var(--color-primary-500)',
                        color: 'white',
                      }}
                    >
                      i
                    </span>
                  </Tooltip>
                </label>
                <input
                  id="employees-ignored"
                  type="number"
                  value={employeesIgnoredCount}
                  readOnly
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                  style={{
                    backgroundColor: 'var(--color-background-paper)',
                    borderColor: 'var(--color-border-light)',
                    color: 'var(--color-text-primary)',
                  }}
                />
              </div>
              <Input
                label="Version"
                type="number"
                value={formData.version?.toString() || '1'}
                onChange={(e) => handleInputChange('version', parseInt(e.target.value) || 1)}
                required
                fullWidth
                readOnly
              />
            </div>
          </div>
        </div>

        <div
          className="rounded-lg border p-5"
          style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Insurance Plan
          </h2>
          <div className="space-y-4">
            <div>
              <div className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Benefits <span className="text-red-500">*</span> (Max 3)
              </div>
              <div className="flex flex-wrap gap-2">
                {BENEFITS_OPTIONS.map((benefit) => (
                  <button
                    key={benefit}
                    type="button"
                    onClick={() => handleBenefitToggle(benefit)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium border',
                      insurancePlan.benefits?.includes(benefit)
                        ? 'bg-[var(--color-primary-500)] text-white border-[var(--color-primary-500)]'
                        : 'border-[var(--color-border-light)]'
                    )}
                    style={{
                      color: insurancePlan.benefits?.includes(benefit)
                        ? 'white'
                        : 'var(--color-text-primary)',
                    }}
                  >
                    {benefit}
                  </button>
                ))}
              </div>
              {formErrors.insurance_benefits && (
                <p className="mt-1 text-xs" style={{ color: 'var(--color-error-500)' }}>
                  {formErrors.insurance_benefits}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Weekly Cap 1"
                type="number"
                step="0.01"
                min={0}
                max={4000}
                value={insurancePlan.weekly_cap_1 || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInsurancePlanChange('weekly_cap_1', value);
                  if (formErrors.weekly_cap_1 && (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 4000))) {
                    setFormErrors((prev) => ({ ...prev, weekly_cap_1: '' }));
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 0 || parseFloat(value) > 4000)) {
                    setFormErrors((prev) => ({ ...prev, weekly_cap_1: 'Weekly cap 1 must be between $0 and $4,000' }));
                  }
                }}
                errorText={formErrors.weekly_cap_1}
                fullWidth
              />
              <Input
                label="Weekly Cap 2"
                type="number"
                step="0.01"
                min={0}
                max={4000}
                value={insurancePlan.weekly_cap_2 || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInsurancePlanChange('weekly_cap_2', value || null);
                  if (formErrors.weekly_cap_2 && (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 4000))) {
                    setFormErrors((prev) => ({ ...prev, weekly_cap_2: '' }));
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 0 || parseFloat(value) > 4000)) {
                    setFormErrors((prev) => ({ ...prev, weekly_cap_2: 'Weekly cap 2 must be between $0 and $4,000' }));
                  }
                }}
                errorText={formErrors.weekly_cap_2}
                fullWidth
              />
              <Input
                label="Weekly Cap 3"
                type="number"
                step="0.01"
                min={0}
                max={4000}
                value={insurancePlan.weekly_cap_3 || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInsurancePlanChange('weekly_cap_3', value || null);
                  if (formErrors.weekly_cap_3 && (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 4000))) {
                    setFormErrors((prev) => ({ ...prev, weekly_cap_3: '' }));
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 0 || parseFloat(value) > 4000)) {
                    setFormErrors((prev) => ({ ...prev, weekly_cap_3: 'Weekly cap 3 must be between $0 and $4,000' }));
                  }
                }}
                errorText={formErrors.weekly_cap_3}
                fullWidth
              />
            </div>

            <div>
              <div className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Length of Leave Weeks <span className="text-red-500">*</span> (Max 3)
              </div>
              <div className="flex flex-wrap gap-2">
                {LENGTH_OF_LEAVE_WEEKS_OPTIONS.map((weeks) => (
                  <button
                    key={weeks}
                    type="button"
                    onClick={() => handleLengthOfLeaveToggle(weeks)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium border',
                      insurancePlan.length_of_leave_weeks?.includes(weeks)
                        ? 'bg-[var(--color-primary-500)] text-white border-[var(--color-primary-500)]'
                        : 'border-[var(--color-border-light)]'
                    )}
                    style={{
                      color: insurancePlan.length_of_leave_weeks?.includes(weeks)
                        ? 'white'
                        : 'var(--color-text-primary)',
                    }}
                  >
                    {weeks} weeks
                  </button>
                ))}
              </div>
              {formErrors.length_of_leave_weeks && (
                <p className="mt-1 text-xs" style={{ color: 'var(--color-error-500)' }}>
                  {formErrors.length_of_leave_weeks}
                </p>
              )}
            </div>

            <div>
              <div className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Length of Leave Weeks Secondary (Max 2)
              </div>
              <div className="flex flex-wrap gap-2">
                {LENGTH_OF_LEAVE_WEEKS_SEC_OPTIONS.map((weeks) => (
                  <button
                    key={weeks}
                    type="button"
                    onClick={() => handleLengthOfLeaveSecToggle(weeks)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium border',
                      insurancePlan.length_of_leave_weeks_sec?.includes(weeks)
                        ? 'bg-[var(--color-primary-500)] text-white border-[var(--color-primary-500)]'
                        : 'border-[var(--color-border-light)]'
                    )}
                    style={{
                      color: insurancePlan.length_of_leave_weeks_sec?.includes(weeks)
                        ? 'white'
                        : 'var(--color-text-primary)',
                    }}
                  >
                    {weeks} weeks
                  </button>
                ))}
              </div>
              {formErrors.length_of_leave_weeks_sec && (
                <p className="mt-1 text-xs" style={{ color: 'var(--color-error-500)' }}>
                  {formErrors.length_of_leave_weeks_sec}
                </p>
              )}
            </div>

            <div>
              <div className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Length of Service <span className="text-red-500">*</span> (Max 2)
              </div>
              <div className="flex flex-wrap gap-2">
                {LENGTH_OF_SERVICE_OPTIONS.map((service) => (
                  <button
                    key={service}
                    type="button"
                    onClick={() => handleLengthOfServiceToggle(service)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium border',
                      insurancePlan.length_of_service?.includes(service)
                        ? 'bg-[var(--color-primary-500)] text-white border-[var(--color-primary-500)]'
                        : 'border-[var(--color-border-light)]'
                    )}
                    style={{
                      color: insurancePlan.length_of_service?.includes(service)
                        ? 'white'
                        : 'var(--color-text-primary)',
                    }}
                  >
                    {service}
                  </button>
                ))}
              </div>
              {formErrors.length_of_service && (
                <p className="mt-1 text-xs" style={{ color: 'var(--color-error-500)' }}>
                  {formErrors.length_of_service}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Row 2: Length of Service | Concurrency with Benefits | Aggregate Benefit */}
              <Select
                label="Concurrency with Benefits"
                options={CONCURRENCY_OPTIONS}
                value={insurancePlan.concurrency_with_benefits || 'concurrently'}
                onChange={(e) => handleInsurancePlanChange('concurrency_with_benefits', e.target.value)}
                required
                fullWidth
              />
              <Select
                label="Aggregate Benefit"
                options={AGGREGATE_BENEFIT_OPTIONS}
                value={insurancePlan.aggregate_benefit || '1000000'}
                onChange={(e) => handleInsurancePlanChange('aggregate_benefit', e.target.value)}
                required
                fullWidth
              />
              {/* Row 3: Private STD Plan | STD Benefit Percentage | Maximum Weekly Payment */}
              <Select
                label="Private STD Plan"
                options={YES_NO_OPTIONS}
                value={insurancePlan.private_std_plan || 'no'}
                onChange={(e) => handleInsurancePlanChange('private_std_plan', e.target.value)}
                required
                fullWidth
              />
              {insurancePlan.private_std_plan === 'yes' && (
                <>
                  <Select
                    label="STD Benefit Percentage"
                    options={[
                      { value: '', label: 'Select benefit %' },
                      ...BENEFITS_OPTIONS.map((b) => ({ value: String(parseFloat(b)), label: b })),
                    ]}
                    value={insurancePlan.pay_for_std_plan ? String(parseFloat(insurancePlan.pay_for_std_plan)) : ''}
                    onChange={(e) => {
                      handleInsurancePlanChange('pay_for_std_plan', e.target.value || null);
                      if (formErrors.pay_for_std_plan && e.target.value) {
                        setFormErrors((prev) => ({ ...prev, pay_for_std_plan: '' }));
                      }
                    }}
                    errorText={formErrors.pay_for_std_plan}
                    required
                    fullWidth
                  />
                  <Input
                    label="Maximum Weekly Payment"
                    type="number"
                    step="0.01"
                    min={100}
                    max={4000}
                    value={insurancePlan.maximum_weekly_payment || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInsurancePlanChange('maximum_weekly_payment', value || null);
                      if (formErrors.maximum_weekly_payment && (value === '' || (parseFloat(value) >= 100 && parseFloat(value) <= 4000))) {
                        setFormErrors((prev) => ({ ...prev, maximum_weekly_payment: '' }));
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 100 || parseFloat(value) > 4000)) {
                        setFormErrors((prev) => ({ ...prev, maximum_weekly_payment: 'Weekly max must be between $100 and $4,000' }));
                      }
                    }}
                    errorText={formErrors.maximum_weekly_payment}
                    required
                    fullWidth
                  />
                  {/* Row 4: Waiting Period Days | SDI Offset | PFL Offset */}
                  <Input
                    label="Waiting Period Days"
                    type="number"
                    min={4}
                    max={30}
                    value={insurancePlan.waiting_period_days?.toString() || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInsurancePlanChange('waiting_period_days', value === '' ? null : parseInt(value) || null);
                      if (formErrors.waiting_period_days && (value === '' || (parseInt(value) >= 4 && parseInt(value) <= 30))) {
                        setFormErrors((prev) => ({ ...prev, waiting_period_days: '' }));
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && (isNaN(parseInt(value)) || parseInt(value) < 4 || parseInt(value) > 30)) {
                        setFormErrors((prev) => ({ ...prev, waiting_period_days: 'Waiting period must be between 4 and 30 days' }));
                      }
                    }}
                    errorText={formErrors.waiting_period_days}
                    required
                    fullWidth
                  />
                </>
              )}
              <Select
                label="SDI Offset"
                options={YES_NO_OPTIONS}
                value={insurancePlan.sdi_offset || 'no'}
                onChange={(e) => handleInsurancePlanChange('sdi_offset', e.target.value)}
                required
                fullWidth
              />
              <Select
                label="PFL Offset"
                options={YES_NO_OPTIONS}
                value={insurancePlan.pfl_offset || 'no'}
                onChange={(e) => handleInsurancePlanChange('pfl_offset', e.target.value)}
                required
                fullWidth
              />
              {/* PRTO-8: Include LMS — Yes | No | Both */}
              <Select
                label="Include LMS"
                options={INCLUDE_LMS_OPTIONS}
                value={insurancePlan.include_lms || 'yes'}
                onChange={(e) => handleInsurancePlanChange('include_lms', e.target.value)}
                required
                fullWidth
              />
            </div>
          </div>
        </div>

        <div
          className="rounded-lg border p-5"
          style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Handbook Plan
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Plan Type"
              options={PLAN_TYPE_OPTIONS}
              value={handbookPlan.plan_type || 'standard'}
              onChange={(e) => handleHandbookPlanChange('plan_type', e.target.value)}
              required
              fullWidth
            />
            <Select
              label="Length of Leave Weeks"
              options={LENGTH_OF_LEAVE_WEEKS_SELECT_OPTIONS}
              value={handbookPlan.length_of_leave_weeks || '16'}
              onChange={(e) => handleHandbookPlanChange('length_of_leave_weeks', e.target.value)}
              errorText={formErrors.handbook_length_of_leave_weeks}
              required
              fullWidth
            />
            {handbookPlan.plan_type === 'primary_and_secondary' && (
              <Select
                label="Length of Leave Weeks Secondary"
                options={LENGTH_OF_LEAVE_WEEKS_SELECT_OPTIONS}
                value={handbookPlan.length_of_leave_weeks_sec || ''}
                onChange={(e) => handleHandbookPlanChange('length_of_leave_weeks_sec', e.target.value || null)}
                errorText={formErrors.handbook_length_of_leave_weeks_sec}
                required
                fullWidth
              />
            )}
            <Input
              label="Benefit (%)"
              type="number"
              step="0.01"
              value={handbookPlan.benefit || ''}
              onChange={(e) => {
                const value = e.target.value;
                handleHandbookPlanChange('benefit', value);
                if (formErrors.handbook_benefit && (value === '' || parseFloat(value) > 0)) {
                  setFormErrors((prev) => ({ ...prev, handbook_benefit: '' }));
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value && parseFloat(value) <= 0) {
                  setFormErrors((prev) => ({ ...prev, handbook_benefit: 'Benefit must be a positive number' }));
                }
              }}
              errorText={formErrors.handbook_benefit}
              required
              fullWidth
            />
            <Input
              label="Weekly Cap"
              type="number"
              step="0.01"
              value={handbookPlan.weekly_cap || ''}
              onChange={(e) => {
                const value = e.target.value;
                handleHandbookPlanChange('weekly_cap', value);
                if (formErrors.handbook_weekly_cap && (value === '' || parseFloat(value) > 0)) {
                  setFormErrors((prev) => ({ ...prev, handbook_weekly_cap: '' }));
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value && parseFloat(value) <= 0) {
                  setFormErrors((prev) => ({ ...prev, handbook_weekly_cap: 'Weekly cap must be a positive number' }));
                }
              }}
              errorText={formErrors.handbook_weekly_cap}
              required
              fullWidth
            />
          </div>
        </div>

        <div className="flex gap-4 justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate('/quotes')}
            className="px-6 py-2 rounded-lg border font-medium"
            style={{
              borderColor: 'var(--color-border-medium)',
              color: 'var(--color-text-primary)',
            }}
          >
            Cancel
          </button>
          {!isEdit && (
            <button
              type="button"
              onClick={() => {
                setFormData({
                  prospect_name: '',
                  quote_name: '',
                  quote_type: 'new',
                  distribution_channel: 'direct',
                  peo_group: null,
                  policy_state: undefined,
                  policy_effective_date: '',
                  status: undefined,
                  employee_count: 0,
                  version: isClone
                    ? prefillData?.next_chain_version || (prefillData?.version || 0) + 1
                    : 1,
                  power_point_template:
                    powerPointTemplates.length > 0 ? powerPointTemplates[0].id : null,
                  source_quote_id: isClone ? prefillData?.id : undefined,
                });
                setInsurancePlan({
                  benefits: [],
                  weekly_cap_1: '',
                  weekly_cap_2: null,
                  weekly_cap_3: null,
                  length_of_leave_weeks: [],
                  length_of_leave_weeks_sec: null,
                  length_of_service: [],
                  concurrency_with_benefits: 'concurrently',
                  private_std_plan: 'no',
                  pay_for_std_plan: null,
                  maximum_weekly_payment: null,
                  waiting_period_days: null,
                  sdi_offset: 'yes',
                  pfl_offset: 'yes',
                  aggregate_benefit: AGGREGATE_BENEFIT_DEFAULT,
                });
                setHandbookPlan({
                  plan_type: 'standard',
                  length_of_leave_weeks: '16',
                  length_of_leave_weeks_sec: null,
                  benefit: '100.00',
                  weekly_cap: '3000',
                });
                setEmployeesIgnoredCount(0);
                setCensusIgnoredReasons(EMPTY_CENSUS_IGNORED_REASONS);
                setFormErrors({});
                setError(null);
                
                if (raterFiles.length > 0) {
                  const mostRecent = raterFiles.sort((a, b) => 
                    new Date(b.created).getTime() - new Date(a.created).getTime()
                  )[0];
                  if (mostRecent) {
                    setFormData((prev) => ({
                      ...prev,
                      rater_file: mostRecent.id,
                    }));
                  }
                }
              }}
              className="px-6 py-2 rounded-lg border font-medium"
              style={{
                borderColor: 'var(--color-border-medium)',
                color: 'var(--color-text-primary)',
              }}
            >
              Reset
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className={cn(
              'px-6 py-2 rounded-lg text-white font-medium',
              submitting && 'opacity-50 cursor-not-allowed'
            )}
            style={{ backgroundColor: 'var(--color-primary-500)' }}
          >
            {submitting ? 'Saving...' : isEdit ? 'Update Quote' : 'Get Quote Outputs'}
          </button>
        </div>
      </form>
    </div>
  );
};

